import { useCallback, useEffect, useMemo, useState } from 'react';

interface UseResendCountdownOptions {
    initialDurationMs: number;
    intervalMs?: number;
}

function formatRemainingTime(totalMilliseconds: number) {
    const totalSeconds = Math.ceil(totalMilliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export function useResendCountdown({
    initialDurationMs,
    intervalMs = 1000,
}: UseResendCountdownOptions) {
    const [availableAt, setAvailableAt] = useState<number>(() => Date.now() + initialDurationMs);
    const [now, setNow] = useState<number>(() => Date.now());

    useEffect(() => {
        const interval = window.setInterval(() => {
            const nextNow = Date.now();

            if (nextNow >= availableAt) {
                setNow(availableAt);
                window.clearInterval(interval);
                return;
            }

            setNow(nextNow);
        }, intervalMs);

        return () => window.clearInterval(interval);
    }, [availableAt, intervalMs]);

    const remainingTimeMs = Math.max(0, availableAt - now);
    const isAvailable = remainingTimeMs === 0;

    const formattedRemainingTime = useMemo(() => {
        if (isAvailable) {
            return null;
        }

        return formatRemainingTime(remainingTimeMs);
    }, [isAvailable, remainingTimeMs]);

    const restart = useCallback(() => {
        const currentTime = Date.now();
        setNow(currentTime);
        setAvailableAt(currentTime + initialDurationMs);
    }, [initialDurationMs]);

    return {
        isAvailable,
        remainingTimeMs,
        formattedRemainingTime,
        restart,
    };
}
